import React, { useState, useRef, useEffect } from 'react';
import { Share2, Copy, Check, Link2, MessageCircle, Twitter, Facebook } from 'lucide-react';
import { toast } from 'sonner';

const ShareButton = ({
  url,
  title = 'InnerGlow — AI Skin Analysis',
  text = 'I found InnerGlow — get a full clinical AI skin analysis in seconds. Check it out 👇',
  variant = 'light',
  className = '',
  label = 'Share',
}) => {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.origin : '');
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (e) { /* user cancelled or unsupported */ }
    }
    setOpen((o) => !o);
  };

  const enc = encodeURIComponent;
  const links = [
    { key: 'x', label: 'X (Twitter)', icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}` },
    { key: 'fb', label: 'Facebook', icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { key: 'wa', label: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/?text=${enc(text + ' ' + shareUrl)}` },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const triggerCls = variant === 'dark'
    ? 'text-white/80 hover:text-white'
    : 'text-[#FF6B4A] hover:text-[#E8552F]';

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={nativeShare}
        className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${triggerCls} ${className}`}
        data-testid="share-button"
        aria-label="Share"
      >
        <Share2 className="w-4 h-4" /> {label}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 right-0 w-52 bg-white rounded-xl shadow-xl border border-[#F3E6E0] p-1.5" data-testid="share-menu">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#1A1514] hover:bg-[#FFF2EC] transition-colors"
              data-testid={`share-${l.key}`}
            >
              <l.icon className="w-4 h-4 text-[#FF6B4A]" /> {l.label}
            </a>
          ))}
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#1A1514] hover:bg-[#FFF2EC] transition-colors"
            data-testid="share-copy"
          >
            {copied ? <Check className="w-4 h-4 text-[#FF6B4A]" /> : <Link2 className="w-4 h-4 text-[#FF6B4A]" />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
